/**
 * Skylite Desktop OS Interface
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 * 
 * This file and its contents are protected by copyright law.
 * Unauthorized reproduction or distribution is prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Breadcrumbs,
  Link,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Folder as FolderIcon,
  CreateNewFolder as NewFolderIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  DriveFileMove as MoveIcon,
  Save as SaveIcon,
  SaveAlt as SaveAsIcon
} from '@mui/icons-material';
import { fileSystem } from '../services/fileSystem';
import FileEditor from './FileEditor';
import { showFileMenu } from '../utils/menuUtils';
import { jsPDF } from 'jspdf';
import { useLanguage } from '../contexts/LanguageContext';

const Files = () => {
  const { t } = useLanguage();
  // Core state
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [activeFile, setActiveFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog states
  const [contextMenu, setContextMenu] = useState(null);
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [moveDialog, setMoveDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [fileInputRef] = useState(React.createRef());

  const refreshFileList = useCallback(async () => {
    try {
      const fileList = await fileSystem.listFiles(currentPath);
      const folderList = fileSystem.listFolders();
      setFiles(fileList);
      setFolders(folderList);
    } catch (error) {
      console.error('Error refreshing file list:', error);
      setSnackbar({
        open: true,
        message: t('errorLoading'),
        severity: 'error'
      });
    }
  }, [currentPath, t]);

  useEffect(() => {
    refreshFileList();
  }, [refreshFileList]);

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setSelectedItem(item);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleDelete = async () => {
    if (selectedItem) {
      try {
        if ('content' in selectedItem) {
          await fileSystem.deleteFile(selectedItem.path);
        } else {
          await fileSystem.deleteFolder(selectedItem);
        }
        await refreshFileList();
        setSnackbar({
          open: true,
          message: t('itemDeleted'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting item:', error);
        setSnackbar({
          open: true,
          message: t('errorDeleting'),
          severity: 'error'
        });
      }
    }
    handleContextMenuClose();
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      fileSystem.createFolder(newFolderName);
      refreshFileList();
      setNewFolderDialog(false);
      setNewFolderName('');
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentPath(folder);
  };

  const handleNavigateBack = () => {
    setCurrentPath('');
  };

  const handleMoveFile = async () => {
    if (selectedItem && selectedFolder) {
      try {
        await fileSystem.moveFile(selectedItem.path, selectedFolder);
        await refreshFileList();
        setMoveDialog(false);
        setSelectedFolder('');
        setSnackbar({
          open: true,
          message: t('fileMoved'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Failed to move file:', error);
        setSnackbar({
          open: true,
          message: t('errorMoving'),
          severity: 'error'
        });
      }
    }
    handleContextMenuClose();
  };

  const handleFileDoubleClick = (file) => {
    setActiveFile(file);
  };

  const handleEditorClose = () => {
    setActiveFile(null);
  };

  const getFileIcon = (file) => {
    if (file.type === 'application/pdf') {
      return <PdfIcon sx={{ color: '#FF5252' }} />;
    }
    return <FileIcon sx={{ color: '#4CAF50' }} />;
  };

  const handleNewFile = () => {
    setNewFolderDialog(true);
    setNewFolderName('');
    setSelectedFolder(currentPath || folders[0] || '');
  };

  const handleFileOpen = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: t('fileTooLarge'),
        severity: 'error'
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result;
      if (content) {
        try {
          const existingFiles = await fileSystem.listFiles(currentPath);
          if (existingFiles.some(f => f.name === file.name)) {
            if (!window.confirm('A file with this name already exists. Do you want to replace it?')) {
              return;
            }
          }

          const savedFile = await fileSystem.saveFile(
            file.name,
            content,
            currentPath,
            file.type || 'text/plain'
          );
          
          setSnackbar({
            open: true,
            message: t('fileUploaded'),
            severity: 'success'
          });
          
          await refreshFileList();
          setActiveFile({
            file: savedFile,
            content: content
          });
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.message,
            severity: 'error'
          });
        }
      }
    };
    reader.onerror = () => {
      setSnackbar({
        open: true,
        message: t('errorUploading'),
        severity: 'error'
      });
    };
    reader.readAsText(file);
  };

  const handleDownloadAsPDF = () => {
    if (!activeFile?.content) {
      setSnackbar({
        open: true,
        message: t('noFileSelected'),
        severity: 'error'
      });
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Add content to PDF
      doc.setFontSize(12);
      const splitText = doc.splitTextToSize(activeFile.content, 180);
      doc.text(splitText, 15, 15);

      // Generate PDF name from original filename
      const pdfName = activeFile.file.name.replace(/\.[^/.]+$/, '') + '.pdf';
      
      // Save PDF
      doc.save(pdfName);

      setSnackbar({
        open: true,
        message: t('fileDownloaded'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('errorDownloading'),
        severity: 'error'
      });
    }
  };

  const handleSave = async () => {
    try {
      // Simple save functionality
      setSnackbar({
        open: true,
        message: t('fileSaved'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('errorSaving'),
        severity: 'error'
      });
    }
  };

  const handleSaveAs = async () => {
    try {
      // Simple save-as functionality
      setSnackbar({
        open: true,
        message: t('fileSaved'),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('errorSaving'),
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      backgroundColor: '#1a1a1a',
      color: '#fff',
      padding: 2,
      overflow: 'auto'
    }}>
      <Typography variant="h6">
        {t('files')}
      </Typography>

      {/* Toolbar */}
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {currentPath && (
            <IconButton 
              onClick={handleNavigateBack}
              sx={{ color: 'text.primary', mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component="button"
              variant="subtitle1"
              color="inherit"
              onClick={handleNavigateBack}
              sx={{ textDecoration: currentPath ? 'underline' : 'none' }}
            >
              {t('files')}
            </Link>
            {currentPath && (
              <Typography variant="subtitle1" color="text.primary">
                {currentPath}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>
        <IconButton 
          onClick={() => setNewFolderDialog(true)}
          sx={{ color: 'text.primary' }}
        >
          <NewFolderIcon />
        </IconButton>
      </Box>

      {/* File List */}
      <List sx={{ flex: 1, overflow: 'auto' }}>
        {/* Folders */}
        {!currentPath && folders.map((folder) => (
          <ListItem
            key={folder}
            disablePadding
            onContextMenu={(e) => handleContextMenu(e, folder)}
          >
            <ListItemButton onClick={() => handleFolderClick(folder)}>
              <ListItemIcon>
                <FolderIcon sx={{ color: '#FFD700' }} />
              </ListItemIcon>
              <ListItemText primary={folder} />
            </ListItemButton>
          </ListItem>
        ))}

        {!currentPath && folders.length > 0 && files.length > 0 && (
          <Divider sx={{ my: 1 }} />
        )}

        {/* Files */}
        {files.map((file) => (
          <ListItem
            key={file.path}
            disablePadding
            onContextMenu={(e) => handleContextMenu(e, file)}
          >
            <ListItemButton onDoubleClick={() => handleFileDoubleClick(file)}>
              <ListItemIcon>
                {getFileIcon(file)}
              </ListItemIcon>
              <ListItemText 
                primary={file.name}
                secondary={new Date(file.modifiedAt).toLocaleString()}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: '#2D2D2D',
            color: 'white',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.4)',
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            },
            '& .MuiListItemIcon-root': {
              color: 'inherit',
              minWidth: '36px'
            }
          }
        }}
      >
        {selectedItem && 'content' in selectedItem && (
          <>
            <MenuItem onClick={handleSave}>
              <ListItemIcon>
                <SaveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('save')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSaveAs}>
              <ListItemIcon>
                <SaveAsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('saveAs')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDownloadAsPDF}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('download')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleContextMenuClose();
              setMoveDialog(true);
            }}>
              <ListItemIcon>
                <MoveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('move')}</ListItemText>
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* New Folder Dialog */}
      <Dialog 
        open={newFolderDialog} 
        onClose={() => setNewFolderDialog(false)}
      >
        <DialogTitle>{t('createFolder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('folderName')}
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder}>{t('createFolder')}</Button>
        </DialogActions>
      </Dialog>

      {/* Move File Dialog */}
      <Dialog 
        open={moveDialog} 
        onClose={() => setMoveDialog(false)}
      >
        <DialogTitle>{t('moveFile')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel>{t('selectFolder')}</InputLabel>
            <Select
              value={selectedFolder}
              label={t('selectFolder')}
              onChange={(e) => setSelectedFolder(e.target.value)}
            >
            {folders.map((folder) => (
                <MenuItem key={folder} value={folder}>
                  {folder}
                </MenuItem>
            ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMoveDialog(false);
            setSelectedFolder('');
          }}>Cancel</Button>
          <Button 
            onClick={handleMoveFile}
            disabled={!selectedFolder}
          >{t('move')}</Button>
        </DialogActions>
      </Dialog>

      {/* File Editor Dialog */}
      <Dialog
        open={Boolean(activeFile)}
        onClose={handleEditorClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh'
          }
        }}
      >
        {activeFile && (
          <FileEditor
            file={activeFile}
            onClose={handleEditorClose}
            fileSystem={fileSystem}
            refreshFileList={refreshFileList}
          />
        )}
      </Dialog>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelected}
        accept=".txt,.md,.json"
      />

      {/* File Menu */}
      {showFileMenu({
        anchorEl: null,
        onClose: () => {},
        handlers: {
          onNew: handleNewFile,
          onOpen: handleFileOpen
        }
      })}

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Files; 